import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';

export interface GlobalOverviewQuestionnairesRouteModel {
  fetchFlowsPage: (page: number, searchTerm: string) => Promise<Array<Flow | GlobalFlow>>;
  flows: GlobalFlow[];
}

export default class GlobalOverviewQuestionnairesRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;
  @service declare store: Store;

  redirect(): void {
    if (this.abilities.cannot('navigate to global questionnaires')) {
      void this.router.replaceWith('unauthorized');
    }
  }

  async model(): Promise<GlobalOverviewQuestionnairesRouteModel> {
    const flows = await this.store.findAll('global-flow');
    return {
      fetchFlowsPage: (page: number, searchTerm: string) => this.fetchFlowsPage(page, searchTerm),
      flows: flows.slice(),
    };
  }

  // Fetch a page of flows, including global flows.
  // The V2 /flows API is not actually paginated, so the `page` parameter is unused, but available
  // in case we ever manage to move to V3.
  // Since global flows & location flows are represented by different models (`GlobalFlow` and `Flow`, respectively),
  // we can't fetch everything in one shot. Instead, use the list of global flows we fetched in the `model` hook,
  // and if we're requesting the first page (`page === 1`), prepend the list of global flows to the list of
  // location flows.
  async fetchFlowsPage(page: number, searchTerm: string): Promise<Array<Flow | GlobalFlow>> {
    const { flows: globalFlows } = <GlobalOverviewQuestionnairesRouteModel>this.modelFor(this.routeName);

    let matchingGlobalFlows = globalFlows;
    if (!isEmpty(searchTerm)) {
      matchingGlobalFlows = globalFlows.filter((flow) => flow.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    const flows = (
      await this.store.query('flow', {
        filter: {
          property_owned: false,
          name: searchTerm,
        },
        include: 'location',
        sort: 'location.name,name',
      })
    ).filter((flow) => !flow.isGlobalChild);
    // await the `location` relationship for all of the flows we just fetched; even though we specified `location` in the
    // `include` param, accessing the relationship (e.g., to read the location name) causes ember-data to fetch the individual
    // relationship, which causes the `name` to not be available when we try to read it. Awaiting the locations here means that
    // everything is resolved and available synchronously.
    await Promise.all(flows.map((flow) => flow.location));

    if (page === 1) {
      return [...matchingGlobalFlows, ...flows];
    } else {
      return flows;
    }
  }
}
